import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * Short confirmations ("Your password has been updated."), shown as toasts in the corner. Same call as the desktop's
 * QStatusBar.showMessage(text, ms), which the status bar this replaced used: `say(text, ms)`.
 */
/** The full report behind a one-line toast, opened by its Details link. */
export interface ToastDetails { title: string; text: string }
export interface Toast { id: number; text: string; details?: ToastDetails }
interface StatusApi { toasts: Toast[]; say(text: string, ms?: number, details?: ToastDetails): void; dismiss(id: number): void }
const Ctx = createContext<StatusApi>({ toasts: [], say: () => {}, dismiss: () => {} });

/** At most this many at once; the oldest goes first. */
const MAX_TOASTS = 3;

export function StatusProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  // A toast with details stays longer: there is something to open.
  const say = useCallback((text: string, ms?: number, details?: ToastDetails) => {
    if (!text) return;
    ms = ms ?? (details ? 12000 : 4000);
    const id = next.current++;
    setToasts((t) => [...t.filter((x) => x.text !== text), { id, text, details }].slice(-MAX_TOASTS));
    if (ms > 0) window.setTimeout(() => dismiss(id), ms);
  }, [dismiss]);
  const value = useMemo(() => ({ toasts, say, dismiss }), [toasts, say, dismiss]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useStatus = () => useContext(Ctx);
